
const EarlySpiritualEducation = () => {
  return (
    <div className="bg-[#fff] w-full flex flex-col">
      <div
        className="relative w-full bg-cover bg-center h-[550px] flex items-center justify-center"
        style={{
           backgroundImage: "url('./images/early-education.png')",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center px-6 md:px-12 lg:px-20 max-w-4xl">
          <h1 className="text-white text-3xl md:text-5xl font-bold leading-tight mb-4">
            How Early Spiritual Education Shapes Lifelong Believers
          </h1>
          <p className="text-gray-200 text-lg md:text-xl">
            Discover how nurturing faith in early childhood builds resilient, compassionate, and lifelong believers.
          </p>
        </div>
      </div>

      <section className="body--container w-full px-4 py-10 md:px-12 lg:px-24 xl:px-32">
        <div className="mx-auto max-w-4xl text-gray-800 leading-relaxed">
          <p className="mb-6">
            In today’s fast-paced world, where children are constantly exposed to technology, social influences, and diverse
            worldviews, the foundation of faith often begins at home and in early spiritual education. Early spiritual education
            is more than memorizing verses or attending religious classes—it shapes values, character, and a personal relationship
            with God from a young age.
          </p>

          <p className="mb-6">
            Understanding how early spiritual guidance impacts lifelong belief can help parents, teachers, and communities nurture
            resilient, compassionate, and committed followers of faith.
          </p>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              1. Laying the Foundation for Faith
            </h2>
            <p>
              Children are like sponges, absorbing lessons, behaviors, and values from their environment. Early spiritual education
              introduces them to God’s love, moral principles, and timeless truths in a safe, nurturing context. Through Bible stories,
              songs, prayers, and interactive lessons, children begin to internalize faith concepts naturally, planting seeds that
              grow throughout life.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              2. Instilling Core Values and Morality
            </h2>
            <p>
              Faith-based education teaches children universal values that guide ethical decisions:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Honesty and integrity:</strong> Learning truth from Scripture helps children act transparently.</li>
              <li><strong>Compassion and empathy:</strong> Stories of kindness and forgiveness teach children to consider others’ needs.</li>
              <li><strong>Responsibility and self-discipline:</strong> Prayer and reflection nurture accountability and self-control.</li>
            </ul>
            <p>
              These values become the moral compass guiding their interactions with family, peers, and society.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              3. Developing a Personal Relationship With God
            </h2>
            <p>
              Early spiritual education fosters a meaningful, personal connection with God. Through prayer, discussion, and reflection,
              children learn to experience faith as a relationship rather than an obligation, creating an anchor during life’s challenges.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              4. Encouraging Lifelong Curiosity About Faith
            </h2>
            <p>
              Starting spiritual education early encourages children to ask questions, explore beliefs, and seek understanding. Programs
              like Sunday School and youth devotionals foster curiosity, building a thoughtful, resilient faith that withstands societal pressures.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              5. Building Resilience and Emotional Strength
            </h2>
            <p>
              Children learn coping, perseverance, and hope through spiritual teachings. Faith provides comfort during challenges,
              and turning to prayer, Scripture, and community support develops emotional resilience that lasts into adulthood.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              6. Fostering a Sense of Purpose and Identity
            </h2>
            <p>
              Spiritual education helps children understand their unique identity and purpose in God’s plan. Lessons that highlight
              individual gifts and callings nurture a strong spiritual identity, guiding choices and habits throughout life.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              7. Encouraging Community and Service
            </h2>
            <p>
              Early exposure to community and service teaches children that faith is relational. Participating in group activities,
              service projects, and charitable initiatives nurtures empathy, teamwork, and lifelong commitment to helping others.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              8. Creating Habits That Last a Lifetime
            </h2>
            <p>
              Regular prayer, scripture reading, worship participation, and reflection become ingrained habits when introduced early,
              creating a stable foundation for continuous spiritual growth and moral guidance throughout life.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              Conclusion: The Lasting Impact of Early Faith Education
            </h2>
            <p>
              Early spiritual education nurtures hearts, shapes character, and fosters lifelong believers. Parents, teachers, and faith
              leaders who invest in children’s spiritual foundation equip them to live out their faith with purpose, integrity, and
              devotion — influencing families, communities, and the world for generations to come.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <div className="max-w-4xl mx-auto flex flex-col lg:flex-row justify-between items-center gap-6">
          <div className="flex flex-wrap gap-2">
            <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Early Spiritual Education</p>
            <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Faith Formation</p>
            <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Character Development</p>
            <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Lifelong Faith</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EarlySpiritualEducation;